import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSave, faTrash } from "@fortawesome/free-solid-svg-icons";
import api from "../services/api";
import InlineFieldTooltip from "../components/InlineFieldTooltip";

const POSITION_LABELS = { Internal: "Internal", External: "External" };

const PAGE_SIZE = 50;

const WashRulesManagement = () => {
  const [slots, setSlots] = useState([]);
  const [washingCosts, setWashingCosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [editing, setEditing] = useState({});
  const [filterCombo, setFilterCombo] = useState("");
  const [filterPosition, setFilterPosition] = useState("");
  const [page, setPage] = useState(1);

  const fetchSlots = async () => {
    try {
      setLoading(true);
      const [slotsRes, costRes] = await Promise.all([
        api.get("/wash-rules/slots"),
        api.get("/washing-costs", {
          params: { limit: 500, is_active: "true" },
        }),
      ]);
      if (slotsRes.data.success) setSlots(slotsRes.data.data || []);
      if (costRes.data.success) setWashingCosts(costRes.data.data || []);
    } catch (err) {
      console.error("Error fetching wash rule slots:", err);
      setError("Failed to load wash rule slots");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSlots();
  }, []);

  const getEditKey = (slot) =>
    `${slot.corrosionDamageCombinationId}|${slot.position ?? ""}`;

  const getEdit = (slot) => {
    const key = getEditKey(slot);
    return editing[key];
  };

  const setEdit = (slot, value) => {
    setEditing((prev) => ({ ...prev, [getEditKey(slot)]: value }));
  };

  const handleSave = async (slot) => {
    const edit = getEdit(slot);
    if (!edit || !edit.washingCostId || edit.frequencyYears < 1) {
      setError("Select a wash procedure and set frequency (≥ 1 year)");
      return;
    }
    setSavingId(
      slot.corrosionDamageCombinationId + "-" + (slot.position ?? ""),
    );
    setError(null);
    try {
      if (slot.washRule) {
        await api.put(`/wash-rules/${slot.washRule.id}`, {
          washingCostId: edit.washingCostId,
          frequencyYears: parseInt(edit.frequencyYears, 10),
          isActive: edit.isActive,
        });
      } else {
        await api.post("/wash-rules", {
          corrosionDamageCombinationId: slot.corrosionDamageCombinationId,
          position: slot.position || null,
          washingCostId: edit.washingCostId,
          frequencyYears: parseInt(edit.frequencyYears, 10),
          isActive: edit.isActive,
        });
      }
      setEditing((prev) => {
        const p = { ...prev };
        delete p[getEditKey(slot)];
        return p;
      });
      fetchSlots();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save wash rule");
    } finally {
      setSavingId(null);
    }
  };

  const handleClear = async (slot) => {
    if (!slot.washRule) return;
    if (
      !window.confirm(
        "Remove wash assignment for this combination and position?",
      )
    )
      return;
    setSavingId(
      slot.corrosionDamageCombinationId + "-" + (slot.position ?? ""),
    );
    setError(null);
    try {
      await api.delete(`/wash-rules/${slot.washRule.id}`);
      setEditing((prev) => {
        const p = { ...prev };
        delete p[getEditKey(slot)];
        return p;
      });
      fetchSlots();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to remove wash rule");
    } finally {
      setSavingId(null);
    }
  };

  const handleToggleActive = async (slot) => {
    if (!slot.washRule) return;
    setSavingId(
      slot.corrosionDamageCombinationId + "-" + (slot.position ?? ""),
    );
    setError(null);
    try {
      await api.put(`/wash-rules/${slot.washRule.id}`, {
        washingCostId: slot.washRule.washingCostId,
        frequencyYears: slot.washRule.frequencyYears,
        isActive: !slot.washRule.isActive,
      });
      fetchSlots();
    } catch (err) {
      setError(
        err.response?.data?.message || "Failed to update wash rule status",
      );
    } finally {
      setSavingId(null);
    }
  };

  const startEdit = (slot) => {
    if (getEdit(slot) !== undefined) return;
    setEdit(
      slot,
      slot.washRule
        ? {
            washingCostId: slot.washRule.washingCostId,
            frequencyYears: slot.washRule.frequencyYears ?? 1,
            isActive: slot.washRule.isActive !== false,
          }
        : { washingCostId: "", frequencyYears: 1, isActive: true },
    );
  };

  const filteredSlots = slots.filter((s) => {
    if (
      filterCombo &&
      s.corrosionDamageCombinationId !== parseInt(filterCombo, 10)
    )
      return false;
    if (filterPosition !== "" && (s.position || "") !== filterPosition)
      return false;
    return true;
  });
  const totalFiltered = filteredSlots.length;
  const totalPages = Math.ceil(totalFiltered / PAGE_SIZE) || 1;
  const paginatedSlots = filteredSlots.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE,
  );

  const uniqueCombos = [
    ...new Map(
      slots.map((s) => [
        s.corrosionDamageCombinationId,
        {
          id: s.corrosionDamageCombinationId,
          combinationCode: s.combinationCode,
        },
      ]),
    ).values(),
  ];
  const uniquePositions = [
    ...new Set(slots.map((s) => s.position).filter(Boolean)),
  ];

  return (
    <div className="p-6">
      <div className="mb-6 flex items-start gap-2">
        <p className="text-gray-600">
          Combinations are auto-generated from Corrosion Damage Combinations x
          position. Assign a wash procedure and frequency per row. Wash is
          skipped in years when a paint job is scheduled for that element.
        </p>
        <InlineFieldTooltip
          page="wash_rules"
          section="page_description"
          fieldKey="wash_rules_overview"
        />
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex justify-between items-center">
          <span>{error}</span>
          <button
            type="button"
            onClick={() => setError(null)}
            className="text-red-500 hover:underline"
          >
            Dismiss
          </button>
        </div>
      )}

      <div className="mb-4 flex flex-wrap gap-4 items-center">
        <select
          value={filterCombo}
          onChange={(e) => {
            setFilterCombo(e.target.value);
            setPage(1);
          }}
          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All combinations</option>
          {uniqueCombos.map((c) => (
            <option key={c.id} value={c.id}>
              {c.combinationCode}
            </option>
          ))}
        </select>
        <select
          value={filterPosition}
          onChange={(e) => {
            setFilterPosition(e.target.value);
            setPage(1);
          }}
          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All positions</option>
          {uniquePositions.map((p) => (
            <option key={p} value={p}>
              {POSITION_LABELS[p] || p}
            </option>
          ))}
        </select>
      </div>

      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {loading ? (
          <div className="p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" />
            <p className="mt-2 text-gray-600">Loading slots...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Combination
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Position
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Wash procedure
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Frequency (years)
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Active
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedSlots.map((slot) => {
                  const key = getEditKey(slot);
                  const edit = getEdit(slot);
                  const isEditing = edit !== undefined && edit !== null;
                  const saving =
                    savingId ===
                    slot.corrosionDamageCombinationId +
                      "-" +
                      (slot.position ?? "");
                  const washName = (id) => {
                    const w = washingCosts.find((wc) => wc.id === id);
                    return (
                      (w && (w.procedureName ?? w.procedure_name)) || (id ?? "")
                    );
                  };

                  return (
                    <tr
                      key={key}
                      className={
                        !slot.washRule && !isEditing ? "bg-gray-50" : ""
                      }
                    >
                      <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                        {slot.combinationCode}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-600">
                        {POSITION_LABELS[slot.position] ?? slot.position}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm">
                        {isEditing ? (
                          <select
                            value={edit?.washingCostId || ""}
                            onChange={(e) =>
                              setEdit(slot, {
                                ...edit,
                                washingCostId: e.target.value
                                  ? parseInt(e.target.value, 10)
                                  : "",
                              })
                            }
                            className="w-full max-w-xs px-2 py-1 border border-gray-300 rounded text-sm"
                          >
                            <option value="">Not set</option>
                            {washingCosts.map((w) => (
                              <option key={w.id} value={w.id}>
                                {w.procedureName ?? w.procedure_name ?? w.id}
                              </option>
                            ))}
                          </select>
                        ) : slot.washRule ? (
                          washName(slot.washRule.washingCostId)
                        ) : (
                          <span className="text-gray-400">Not set</span>
                        )}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm">
                        {isEditing ? (
                          <input
                            type="number"
                            min={1}
                            value={edit?.frequencyYears ?? 1}
                            onChange={(e) =>
                              setEdit(slot, {
                                ...edit,
                                frequencyYears: e.target.value,
                              })
                            }
                            className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                        ) : slot.washRule ? (
                          slot.washRule.frequencyYears
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm">
                        {isEditing ? (
                          <button
                            type="button"
                            role="switch"
                            aria-checked={edit?.isActive !== false}
                            onClick={() =>
                              setEdit(slot, {
                                ...edit,
                                isActive: !(edit?.isActive !== false),
                              })
                            }
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                              edit?.isActive !== false
                                ? "bg-blue-600"
                                : "bg-gray-300"
                            }`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                edit?.isActive !== false
                                  ? "translate-x-6"
                                  : "translate-x-1"
                              }`}
                            />
                          </button>
                        ) : slot.washRule ? (
                          <button
                            type="button"
                            role="switch"
                            aria-checked={slot.washRule.isActive}
                            disabled={saving}
                            onClick={() => handleToggleActive(slot)}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ${
                              slot.washRule.isActive
                                ? "bg-blue-600"
                                : "bg-gray-300"
                            }`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                slot.washRule.isActive
                                  ? "translate-x-6"
                                  : "translate-x-1"
                              }`}
                            />
                          </button>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-right text-sm">
                        {isEditing ? (
                          <>
                            <button
                              type="button"
                              disabled={
                                saving ||
                                !edit?.washingCostId ||
                                (edit?.frequencyYears || 0) < 1
                              }
                              onClick={() => handleSave(slot)}
                              className="text-blue-600 hover:text-blue-800 disabled:opacity-50 mr-2"
                              title="Save"
                            >
                              <FontAwesomeIcon icon={faSave} />
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                setEditing((prev) => {
                                  const p = { ...prev };
                                  delete p[getEditKey(slot)];
                                  return p;
                                })
                              }
                              className="text-gray-500 hover:text-gray-700"
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              type="button"
                              onClick={() => startEdit(slot)}
                              className="text-blue-600 hover:text-blue-800 mr-2"
                              title="Edit"
                            >
                              Edit
                            </button>
                            {slot.washRule && (
                              <button
                                type="button"
                                disabled={saving}
                                onClick={() => handleClear(slot)}
                                className="text-red-600 hover:text-red-800 disabled:opacity-50"
                                title="Remove wash"
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </button>
                            )}
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {!loading && totalFiltered > PAGE_SIZE && (
        <div className="mt-4 flex items-center justify-between border-t border-gray-200 pt-4 bg-white px-4 py-3 rounded-b-lg">
          <p className="text-sm text-gray-700">
            Showing {(page - 1) * PAGE_SIZE + 1} to{" "}
            {Math.min(page * PAGE_SIZE, totalFiltered)} of {totalFiltered}
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50"
            >
              Previous
            </button>
            <span className="px-3 py-1 text-sm">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
      {!loading && slots.length > 0 && (
        <p className="mt-2 text-sm text-gray-500">
          {filteredSlots.length} slot(s) — combinations from Corrosion Damage
          Combinations × positions (Internal, External).
        </p>
      )}
    </div>
  );
};

export default WashRulesManagement;
